import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { tokenReceived, userReceived, loggedOut } from "@/store/auth";
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query";
import authConfig, { expiredTokenErrorMessage } from "@/config/auth.ts";
import type { ApiResponse, Enum } from "@/types/api/types.ts";

// Needed for refresh token request
const baseQueryWithoutAuth = fetchBaseQuery({
  baseUrl: "/api/",
  credentials: "include",
});

const baseQuery = fetchBaseQuery({
  baseUrl: "/api/",
  prepareHeaders: (headers) => {
    const token = localStorage.getItem(authConfig.storageTokenKeyName);
    if (token) headers.set("authorization", `Bearer ${token}`);
    if (!headers.has("accept")) {
      headers.set("accept", "application/ld+json");
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  const errorData = result?.error?.data as
    | { error?: string; detail?: string }
    | undefined;

  const hasToRefresh = errorData?.error === expiredTokenErrorMessage;

  const isUnauthenticated =
    result?.error?.status === 401 &&
    errorData?.detail ===
      "Full authentication is required to access this resource.";

  if (isUnauthenticated) {
    api.dispatch(loggedOut());
    window.location.href = "/login";
    return result;
  }

  if (hasToRefresh) {
    const refreshResult = await baseQueryWithoutAuth(
      {
        body: {},
        url: authConfig.refreshTokenEndpoint,
        method: "POST",
        credentials: "include",
      },
      api,
      extraOptions
    );

    if (refreshResult.data) {
      api.dispatch(
        tokenReceived({
          accessToken: (refreshResult.data as { token: string }).token,
        })
      );

      // Rejoue la requête originale avec le nouveau token
      result = await baseQuery(args, api, extraOptions);
    } else {
      api.dispatch(loggedOut());
      window.location.href = "/login";
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  tagTypes: [
    "Users",
    "Roles",
    "Enums",
    "Campaigns",
    "Modules",
    "Participations",
    "LaboratoryUsers",
    "Laboratory",
    "Questionnaire",
    "Mindmeister",
    "QuestionnaireSession",
    "Flags",
    "Programs",
    "Documents",
    "HrdLaboratories",
    "HrdFormulaire",
    "HrdCases",
  ],
  endpoints: (builder) => ({
    getEnums: builder.query<ApiResponse<Enum>, string>({
      query: () => "enums",
      providesTags: ["Enums"],
    }),
    deleteElement: builder.mutation({
      query(iri: string) {
        return {
          url: iri,
          method: "DELETE",
        };
      },
    }),
  }),
});

export const { useGetEnumsQuery, useDeleteElementMutation } = api;
